import React, { useState, useEffect, useRef } from "react";
import { Menu, X, Phone, Globe, ChevronDown } from "lucide-react";
import { useLang } from "@/App";
import { Button } from "@/components/ui/button";

const LANG_LABELS = { es: "ES", va: "VA", en: "EN" };

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const navLinks = [
    { href: "#opiniones", label: t.nav.reviews, id: "reviews" },
    { href: "#servicios", label: t.nav.services, id: "services" },
    { href: "#infantil", label: t.kids.kicker, id: "kids" },
    { href: "#sobre", label: t.nav.about, id: "about" },
    { href: "#marcas", label: t.nav.brands, id: "brands" },
    { href: "#contacto", label: t.nav.contact, id: "contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-white/80 backdrop-blur-sm"
      }`}
      data-testid="site-header"
    >
      {new Date() < new Date("2026-08-24T00:00:00") && (
        <div
          className="bg-amber-400 text-blue-950 text-center text-sm md:text-base font-bold px-4 py-2"
          data-testid="holiday-notice-bar"
        >
          {t.notice}
        </div>
      )}
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="logo-link">
          <img
            src="https://customer-assets.emergentagent.com/job_audicion-amplisoon/artifacts/vici73nm_AMPLISOON.png"
            alt="Amplisoon Centro Audiológico"
            className="h-12 md:h-14 w-auto rounded-lg"
            data-testid="logo-image"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.id}
              href={l.href}
              className="text-base font-medium text-slate-700 hover:text-blue-900 transition-colors"
              data-testid={`nav-${l.id}-link`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <div
            ref={langRef}
            className="relative"
            data-testid="lang-switcher"
          >
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 h-10 rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:border-blue-900 hover:text-blue-900 transition-colors"
              data-testid="lang-toggle"
            >
              <Globe className="w-4 h-4" />
              {LANG_LABELS[lang] || lang.toUpperCase()}
              <ChevronDown className={`w-4 h-4 transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-32 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden" data-testid="lang-dropdown">
                {["es", "va", "en"].map((l) => (
                  <button
                    key={l}
                    onClick={() => {
                      setLang(l);
                      setLangOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm font-semibold transition-colors ${
                      lang === l ? "bg-blue-900 text-white" : "text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid={`lang-${l}-btn`}
                  >
                    {LANG_LABELS[l]}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button
            asChild
            className="h-12 px-6 bg-blue-900 hover:bg-blue-800 text-white text-base font-semibold rounded-xl"
            data-testid="header-book-cta"
          >
            <a href="#contacto">{t.nav.bookCta}</a>
          </Button>
        </div>

        <button
          className="lg:hidden p-2 text-slate-700"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="menu"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="px-5 py-5 flex flex-col gap-4">
            {navLinks.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-lg font-medium text-slate-800 py-2"
                data-testid={`mobile-nav-${l.id}`}
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
              {["es", "va", "en"].map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`px-4 py-2 text-sm font-semibold rounded-full ${
                    lang === l ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-700"
                  }`}
                  data-testid={`mobile-lang-${l}`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <a
              href="tel:+34965637618"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 bg-blue-900 text-white rounded-xl font-semibold"
              data-testid="mobile-call-cta"
            >
              <Phone className="w-5 h-5" />
              965 637 618
            </a>
            <a
              href="#contacto"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center h-12 px-5 bg-sky-600 text-white rounded-xl font-semibold"
              data-testid="mobile-book-cta"
            >
              {t.nav.bookCta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
